import { getCurrentFocusKey } from "@noriginmedia/norigin-spatial-navigation"
import { MutableRefObject, useCallback } from "react"

import { PublicationLevel } from "../generated/graphql"
import { DetailPage } from "../screens/Detail/DetailStore"
import { appStore, useAuth } from "../store/AppStore"
import { comb, isTicketValid } from "../utilities/common"
import { getExpiresOn } from "../utilities/token"

import { useUserSubscriptionStatus } from "./use-user-subscription-status"

export const useAccessCheck = ({
  loginOpen,
  subscribeOpen,
  focusKey,
}: {
  loginOpen: comb<boolean>
  subscribeOpen: comb<boolean>
  focusKey: MutableRefObject<string | null>
}) => {
  const accessTokenState = appStore((state) => state.accessTokenState)
  const currentUser = appStore((state) => state.currentUser)
  const setSpinner = appStore((state) => state.setSpinner)
  const { onTokenExpired } = useAuth()
  const { refreshCurrentUserAndGetSubscriptionState, getCurrentUser } =
    useUserSubscriptionStatus()

  const loginPrompt = useCallback(() => {
    focusKey.current = getCurrentFocusKey()
    loginOpen.set(true)
  }, [])

  const subscriptionPrompt = useCallback(() => {
    focusKey.current = getCurrentFocusKey()
    subscribeOpen.set(true)
  }, [])

  const accessCheck = useCallback(
    async ({
      page,
      skipExpiryCheck = false,
    }: {
      page?: DetailPage
      skipExpiryCheck?: boolean
    }) => {
      if (!page || !page.publicationLevel) return false
      if (!skipExpiryCheck) {
        const expiryDateTime = getExpiresOn()
        if (expiryDateTime) {
          const diffMs = Math.abs(Date.now() - expiryDateTime.getTime())
          const fourHoursMs = 4 * 60 * 60 * 1000
          if (diffMs < fourHoursMs) {
            await onTokenExpired()
            accessCheck({ page, skipExpiryCheck: true })
          }
        }
      }
      if (
        (page.publicationLevel === PublicationLevel.AccountRequired ||
          page.publicationLevel === PublicationLevel.TicketRequired) &&
        !accessTokenState
      ) {
        loginPrompt()
        return false
      }
      if (
        page.publicationLevel === PublicationLevel.TicketRequired &&
        (!currentUser || !isTicketValid(currentUser.ticket?.validUntil))
      ) {
        setSpinner("soft")
        const subbed = await refreshCurrentUserAndGetSubscriptionState()
        setSpinner("out")
        if (subbed) return true
        subscriptionPrompt()
        return false
      }
      return true
    },
    [currentUser, accessTokenState, loginPrompt, subscriptionPrompt],
  )

  return {
    accessCheck,
    refreshCurrentUserAndGetSubscriptionState,
    getCurrentUser,
  }
}
